"use client";

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import {
  CheckIcon,
  ChevronRightIcon,
  DotFilledIcon,
} from "@radix-ui/react-icons";
import * as React from "react";

import {
  OVERLAY_LIST_ROOT_CLASS,
  OVERLAY_OPTION_ITEM_SHAPE_CLASS,
  OVERLAY_OPTION_LIST_INSET_CLASS,
  OVERLAY_OPTION_SEPARATOR_CLASS,
} from "../overlay-option-list";
import { useOverlayPlacement } from "../overlay-placement";
import { useOverlayPortal } from "../overlay-portal";
import {
  cn,
  confirmPopoverListSearchEnter,
  focusPopoverListSearchOnOpen,
  searchableMenuItemPointerProps,
} from "../utils";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
    inset?: boolean;
  }
>(({ className, inset, children, ...props }, ref) => (
  <DropdownMenuPrimitive.SubTrigger
    ref={ref}
    className={cn(
      "flex cursor-pointer select-none items-center px-2 py-1.5 text-left text-sm outline-none focus:bg-accent data-[state=open]:bg-accent data-[disabled]:cursor-default",
      OVERLAY_OPTION_ITEM_SHAPE_CLASS,
      inset && "pl-8",
      className,
    )}
    {...props}
  >
    {children}
    <ChevronRightIcon className="ml-auto h-4 w-4" />
  </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName =
  DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuSubContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & {
    onOpenAutoFocus?: (event: Event) => void;
  }
>(({ className, onOpenAutoFocus, onKeyDown, ...props }, ref) => {
  const openAutoFocusProps = {
    onOpenAutoFocus: (event: Event): void => {
      onOpenAutoFocus?.(event);
      focusPopoverListSearchOnOpen(event);
    },
  };
  return (
    <DropdownMenuPrimitive.SubContent
      collisionPadding={12}
      ref={ref}
      data-popover-list-root=""
      className={cn(
        "pointer-events-auto z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        OVERLAY_OPTION_LIST_INSET_CLASS,
        OVERLAY_LIST_ROOT_CLASS,
        className,
      )}
      {...props}
      {...openAutoFocusProps}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        confirmPopoverListSearchEnter(event);
      }}
    />
  );
});
DropdownMenuSubContent.displayName =
  DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & {
    /** Render inside this element instead of body (fixes DropdownMenu inside Dialog) */
    container?: HTMLElement | null;
    /**
     * Runs before Radix moves focus into the opened menu. Radix forwards the
     * prop to its Menu content at runtime but keeps it out of the public
     * DropdownMenu type, so it is declared here and passed down as a spread.
     */
    onOpenAutoFocus?: (event: Event) => void;
  }
>(
  (
    {
      className,
      sideOffset = 4,
      container,
      onOpenAutoFocus,
      onKeyDown,
      ...props
    },
    ref,
  ) => {
    const placement = useOverlayPlacement();
    const portal = useOverlayPortal(container);
    // A spread is not excess-property-checked, unlike a literal attribute —
    // no cast needed for a prop Radix accepts but does not declare.
    const openAutoFocusProps = {
      onOpenAutoFocus: (event: Event): void => {
        onOpenAutoFocus?.(event);
        focusPopoverListSearchOnOpen(event);
      },
    };
    return (
      <DropdownMenuPrimitive.Portal container={portal}>
        <DropdownMenuPrimitive.Content
          collisionPadding={12}
          ref={ref}
          sideOffset={placement?.sideOffset ?? sideOffset}
          data-popover-list-root=""
          className={cn(
            "pointer-events-auto z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md",
            OVERLAY_OPTION_LIST_INSET_CLASS,
            OVERLAY_LIST_ROOT_CLASS,
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className,
          )}
          {...props}
          {...openAutoFocusProps}
          {...(placement
            ? {
                // A region placement (task details column) wins over the caller's own.
                side: placement.side,
                align: placement.align,
                sticky: "always" as const,
              }
            : {})}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            confirmPopoverListSearchEnter(event);
          }}
        />
      </DropdownMenuPrimitive.Portal>
    );
  },
);
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      OVERLAY_OPTION_ITEM_SHAPE_CLASS,
      inset && "pl-8",
      className,
    )}
    {...props}
    {...searchableMenuItemPointerProps(props)}
    data-popover-list-option=""
  />
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
  <DropdownMenuPrimitive.CheckboxItem
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center py-1.5 pl-8 pr-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      OVERLAY_OPTION_ITEM_SHAPE_CLASS,
      className,
    )}
    checked={checked}
    {...props}
    {...searchableMenuItemPointerProps(props)}
    data-popover-list-option=""
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName =
  DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.RadioItem
    ref={ref}
    className={cn(
      "relative flex cursor-pointer select-none items-center py-1.5 pl-8 pr-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      OVERLAY_OPTION_ITEM_SHAPE_CLASS,
      className,
    )}
    {...props}
    {...searchableMenuItemPointerProps(props)}
    data-popover-list-option=""
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <DotFilledIcon className="h-4 w-4 fill-current" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
    inset?: boolean;
  }
>(({ className, inset, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={cn(
      "px-2 py-1.5 text-sm font-semibold",
      inset && "pl-8",
      className,
    )}
    {...props}
  />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitive.Separator
    ref={ref}
    className={cn(OVERLAY_OPTION_SEPARATOR_CLASS, className)}
    {...props}
  />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn("ml-auto text-xs tracking-widest opacity-60", className)}
      {...props}
    />
  );
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuRadioGroup,
};
